<script setup lang="ts">
/**
 * The AI 助手's panel, floating over the console's bottom right (the whole
 * screen on a phone). Three faces: home — a greeting, 我能帮你做 (three
 * starting points, drawn icons, the first one pictured) and 你可能想问
 * (the admin's suggestions); a
 * conversation, the answer streaming in as it is written, 停止 while it
 * is; and the history of the customer's conversations. A conversation
 * goes on where it was left when the panel is opened again.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Message } from '@arco-design/web-vue'
import {
  IconArrowUp,
  IconClose,
  IconCopy,
  IconDelete,
  IconExpand,
  IconHistory,
  IconLeft,
  IconPlus,
  IconRecordStop,
  IconRight,
  IconShrink,
} from '@arco-design/web-vue/es/icon'
import { assistantApi, type AssistantConversation, type AssistantInfo } from '@/api/assistant'
import { assistantName } from '@/utils/assistant'
import { renderMarkdown } from '@/utils/markdown'
import { formatDateTime } from '@/utils/format'
import { isPhone } from '@/utils/responsive'
import AssistantMark from './AssistantMark.vue'
import AssistantActionIcon from './AssistantActionIcon.vue'
import AssistantActionArt from './AssistantActionArt.vue'

const props = withDefaults(defineProps<{ info: AssistantInfo; open: boolean; from?: 'float' | 'navbar' }>(), { from: 'float' })
const emit = defineEmits<{ close: []; asked: [] }>()
const { t, locale } = useI18n()

const name = computed(() => assistantName(props.info, locale.value, t('assistant.defaultName')))
// The admin's suggested questions in the console's language.
const suggestions = computed(() =>
  (locale.value.startsWith('en') ? props.info.suggestions_en : props.info.suggestions) ?? [],
)
const expanded = ref(false)

// The header's tooltips, held in hand: closing the panel closes them. Left
// to themselves, the one under the pointer stayed open as the panel went
// and, its button gone, drew itself at the window's left edge.
const tip = ref('')
function onTip(key: string, v: boolean) {
  if (v) tip.value = key
  else if (tip.value === key) tip.value = ''
}
const tipOn = (key: string) => props.open && tip.value === key
watch(
  () => props.open,
  (v) => {
    if (!v) tip.value = ''
  },
)

// ---- the conversation ------------------------------------------------------------

type Face = 'home' | 'chat' | 'history'
const face = ref<Face>('home')
interface Line {
  key: number
  role: 'user' | 'assistant'
  content: string
  /** waiting: asked, nothing back yet; streaming: the answer coming. */
  status: 'ok' | 'stopped' | 'failed' | 'waiting' | 'streaming'
  thinking?: boolean
  error?: string
  /** An answer's question, for 重试. */
  question?: string
}
const lines = ref<Line[]>([])
const conversation = ref<{ id: number; title: string } | null>(null)
const busy = ref(false)
let controller: AbortController | null = null
let seq = 0

const draft = ref('')

// 跑马灯's second kind (the admin's choice): while the box is empty, the
// suggested questions turn over in it, and Enter or 发送 asks the one
// showing.
const hints = computed(() => (props.info.input_hints ? suggestions.value : []))
const hintAt = ref(0)
// An input method's word in the making is in the box but not yet in draft:
// no hint under it either.
const composing = ref(false)
const showHint = computed(() => hints.value.length > 0 && !draft.value && !composing.value)
const hint = computed(() => hints.value[hintAt.value % Math.max(1, hints.value.length)] ?? '')
let hintTimer = 0
onMounted(() => {
  hintTimer = window.setInterval(() => {
    if (props.open && showHint.value && hints.value.length > 1) hintAt.value = (hintAt.value + 1) % hints.value.length
  }, 3200)
})
onBeforeUnmount(() => clearInterval(hintTimer))
const left = computed(() => (props.info.daily_limit > 0 ? Math.max(0, props.info.daily_limit - props.info.used_today) : null))

async function ask(text: string) {
  const q = text.trim()
  if (!q || busy.value) return
  draft.value = ''
  face.value = 'chat'
  lines.value.push({ key: ++seq, role: 'user', content: q, status: 'ok' })
  lines.value.push({ key: ++seq, role: 'assistant', content: '', status: 'waiting', question: q })
  // The reactive copy, so each piece redraws.
  const ans = lines.value[lines.value.length - 1]
  stick = true
  scrollDown()
  busy.value = true
  controller = new AbortController()
  try {
    await assistantApi.chat(
      { conversation_id: conversation.value?.id, message: q },
      {
        meta: (d) => (conversation.value = { id: d.conversation_id, title: d.title }),
        thinking: () => (ans.thinking = true),
        delta: (piece) => {
          ans.status = 'streaming'
          ans.content += piece
          scrollDown()
        },
        done: (d) => {
          ans.status = d.status === 'stopped' ? 'stopped' : 'ok'
          emit('asked')
        },
        error: (e) => {
          ans.status = 'failed'
          ans.error = e.message
          emit('asked')
        },
      },
      controller.signal,
    )
    if (ans.status === 'waiting' || ans.status === 'streaming') ans.status = 'ok'
  } catch (e: any) {
    if (e?.name === 'AbortError') {
      ans.status = 'stopped'
      emit('asked')
    } else {
      ans.status = 'failed'
      ans.error = e?.message ?? t('common.loadFailed')
    }
  } finally {
    busy.value = false
    controller = null
    scrollDown()
  }
}

function stop() {
  controller?.abort()
}

function startOver() {
  stop()
  conversation.value = null
  lines.value = []
  face.value = 'home'
}

function onKey(e: KeyboardEvent) {
  // Enter sends; Shift+Enter is a new line; an IME's Enter picks a word.
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault()
    void ask(draft.value || (showHint.value ? hint.value : ''))
  }
}

async function copy(text: string) {
  try {
    await navigator.clipboard.writeText(text)
    Message.success(t('assistant.copied'))
  } catch {
    Message.error(t('common.loadFailed'))
  }
}

// 我能帮你做: two ask straight away; 报错诊断 starts the question for the
// customer to finish.
const ACTIONS = ['onboard', 'diagnose', 'billing'] as const
const input = ref<HTMLElement>()
function act(key: (typeof ACTIONS)[number]) {
  if (key === 'diagnose') {
    draft.value = t('assistant.actDiagnosePrefill')
    void nextTick(() => {
      const el = input.value?.querySelector('textarea')
      el?.focus()
      el?.setSelectionRange(draft.value.length, draft.value.length)
    })
    return
  }
  void ask(t(key === 'onboard' ? 'assistant.actOnboardAsk' : 'assistant.actBillingAsk'))
}

// ---- following the answer --------------------------------------------------------

const body = ref<HTMLElement>()
// Stuck to the bottom while the customer has not scrolled up to read.
let stick = true
function onScroll() {
  const el = body.value
  if (el) stick = el.scrollHeight - el.scrollTop - el.clientHeight < 48
}
function scrollDown() {
  void nextTick(() => {
    const el = body.value
    if (el && stick) el.scrollTop = el.scrollHeight
  })
}

// ---- history ---------------------------------------------------------------------

const history = ref<AssistantConversation[]>([])
const loadingHistory = ref(false)
async function openHistory() {
  face.value = 'history'
  loadingHistory.value = true
  try {
    history.value = await assistantApi.conversations()
  } catch (e: any) {
    Message.error(e?.message ?? t('common.loadFailed'))
  } finally {
    loadingHistory.value = false
  }
}
async function openConversation(c: AssistantConversation) {
  stop()
  try {
    const th = await assistantApi.thread(c.id)
    conversation.value = { id: th.conversation.id, title: th.conversation.title }
    let q = ''
    lines.value = th.messages.map((m) => {
      if (m.role === 'user') q = m.content
      return {
        key: ++seq,
        role: m.role,
        content: m.content,
        status: m.status,
        // The reason's details are not kept: a sentence of its own.
        error: m.status === 'failed' ? t('assistant.failedBefore') : undefined,
        question: m.role === 'assistant' ? q : undefined,
      }
    })
    face.value = 'chat'
    stick = true
    scrollDown()
  } catch (e: any) {
    Message.error(e?.message ?? t('common.loadFailed'))
  }
}
async function remove(c: AssistantConversation) {
  try {
    await assistantApi.remove(c.id)
    history.value = history.value.filter((x) => x.id !== c.id)
    if (conversation.value?.id === c.id) {
      conversation.value = null
      lines.value = []
    }
    Message.success(t('assistant.deleted'))
  } catch (e: any) {
    Message.error(e?.message ?? t('common.loadFailed'))
  }
}
function backFromHistory() {
  face.value = lines.value.length ? 'chat' : 'home'
}

watch(face, () => {
  if (face.value !== 'history') scrollDown()
})

const when = (iso: string) => formatDateTime(iso).slice(5, 16)

// An answer being written ends on a caret, right after its last word
// (a private-use character survives the escaping, then becomes the caret).
const CARET = '\uE000'
function draw(l: Line) {
  if (l.status !== 'streaming') return renderMarkdown(l.content)
  return renderMarkdown(l.content + CARET).replace(CARET, '<span class="md-caret"></span>')
}
</script>

<template>
  <section
    class="assistant-panel"
    :class="{ closed: !open, expanded, phone: isPhone, 'from-navbar': from === 'navbar' }"
    role="dialog"
    :aria-label="name"
    :aria-hidden="!open"
  >
    <header class="head">
      <AssistantMark :icon="info.icon" :animated="!!info.animated && busy" busy :size="24" />
      <span class="title">{{ name }}</span>
      <a-tooltip :content="t('assistant.newChat')" mini :popup-visible="tipOn('new')" @popup-visible-change="(v: boolean) => onTip('new', v)">
        <a-button class="tool" type="text" :disabled="face === 'home' && !lines.length" :aria-label="t('assistant.newChat')" @click="startOver">
          <template #icon><IconPlus /></template>
        </a-button>
      </a-tooltip>
      <a-tooltip :content="t('assistant.history')" mini :popup-visible="tipOn('history')" @popup-visible-change="(v: boolean) => onTip('history', v)">
        <a-button class="tool" type="text" :class="{ on: face === 'history' }" :aria-label="t('assistant.history')" @click="face === 'history' ? backFromHistory() : openHistory()">
          <template #icon><IconHistory /></template>
        </a-button>
      </a-tooltip>
      <a-tooltip
        v-if="!isPhone"
        :content="t(expanded ? 'assistant.shrink' : 'assistant.expand')"
        mini
        :popup-visible="tipOn('expand')"
        @popup-visible-change="(v: boolean) => onTip('expand', v)"
      >
        <a-button class="tool" type="text" :aria-label="t(expanded ? 'assistant.shrink' : 'assistant.expand')" @click="expanded = !expanded">
          <template #icon><IconShrink v-if="expanded" /><IconExpand v-else /></template>
        </a-button>
      </a-tooltip>
      <a-tooltip :content="t('assistant.close')" mini :popup-visible="tipOn('close')" @popup-visible-change="(v: boolean) => onTip('close', v)">
        <a-button class="tool" type="text" :aria-label="t('assistant.close')" @click="(tip = ''), emit('close')">
          <template #icon><IconClose /></template>
        </a-button>
      </a-tooltip>
    </header>

    <!-- History -->
    <div v-if="face === 'history'" class="body">
      <div class="history-head">
        <a class="back" @click="backFromHistory"><IconLeft />{{ t('assistant.back') }}</a>
        <span class="history-title">{{ t('assistant.history') }}</span>
      </div>
      <a-spin :loading="loadingHistory" class="block">
        <a-empty v-if="!loadingHistory && !history.length" :description="t('assistant.historyEmpty')" class="empty" />
        <div v-else class="history">
          <div v-for="c in history" :key="c.id" class="history-item" :class="{ on: conversation?.id === c.id }" @click="openConversation(c)">
            <span class="history-text">{{ c.title }}</span>
            <span class="history-time">{{ when(c.updated_at) }}</span>
            <a-popconfirm :content="t('assistant.deleteConfirm')" position="left" @ok="remove(c)">
              <a-button class="history-del" type="text" size="mini" :aria-label="t('common.delete')" @click.stop>
                <template #icon><IconDelete /></template>
              </a-button>
            </a-popconfirm>
          </div>
        </div>
      </a-spin>
    </div>

    <!-- Home -->
    <div v-else-if="face === 'home'" class="body home">
      <div class="hello">
        <AssistantMark :icon="info.icon" :animated="info.animated" :size="36" />
        <div class="hello-title">{{ t('assistant.hello', { name }) }}</div>
        <div class="hello-sub">{{ t('assistant.helloSub') }}</div>
      </div>
      <div class="section">{{ t('assistant.canDo') }}</div>
      <div class="actions">
        <button v-for="(a, i) in ACTIONS" :key="a" type="button" class="action" :class="{ featured: i === 0 }" @click="act(a)">
          <AssistantActionIcon :name="a" :size="26" class="action-icon" />
          <AssistantActionArt v-if="i === 0" class="action-art" />
          <span class="action-text">
            <span class="action-title">{{ t(`assistant.act_${a}`) }}</span>
            <span class="action-sub">{{ t(`assistant.act_${a}Sub`) }}</span>
          </span>
        </button>
      </div>
      <template v-if="suggestions.length">
        <div class="section">{{ t('assistant.mayAsk') }}</div>
        <div class="suggestions">
          <button v-for="q in suggestions" :key="q" type="button" class="suggestion" @click="ask(q)">
            <span class="suggestion-text">{{ q }}</span>
            <IconRight class="suggestion-arrow" />
          </button>
        </div>
      </template>
    </div>

    <!-- A conversation -->
    <div v-else ref="body" class="body chat" @scroll="onScroll">
      <template v-for="l in lines" :key="l.key">
        <div v-if="l.role === 'user'" class="q">
          <div class="q-text">{{ l.content }}</div>
        </div>
        <div v-else class="a">
          <AssistantMark
            :icon="info.icon"
            :animated="!!info.animated && (l.status === 'waiting' || l.status === 'streaming')"
            busy
            :size="24"
            class="a-mark"
          />
          <div class="a-main">
            <div v-if="l.status === 'waiting'" class="waiting">
              {{ l.thinking ? t('assistant.thinking') : t('assistant.waiting') }}<span class="dots"><i /><i /><i /></span>
            </div>
            <!-- eslint-disable-next-line vue/no-v-html — escaped by renderMarkdown before any formatting -->
            <div v-if="l.content" class="md" v-html="draw(l)" />
            <div v-if="l.status === 'failed'" class="a-error">
              {{ l.error || t('common.loadFailed') }}
              <a v-if="l.question && !busy" class="a-retry" @click="ask(l.question)">{{ t('assistant.retry') }}</a>
            </div>
            <div v-else-if="l.status === 'stopped'" class="a-note">{{ t('assistant.stopped') }}</div>
            <div v-if="l.content && (l.status === 'ok' || l.status === 'stopped')" class="a-tools">
              <a-tooltip :content="t('assistant.copy')" mini>
                <a-button type="text" size="mini" :aria-label="t('assistant.copy')" @click="copy(l.content)">
                  <template #icon><IconCopy /></template>
                </a-button>
              </a-tooltip>
            </div>
          </div>
        </div>
      </template>
    </div>

    <footer v-if="face !== 'history'" class="foot">
      <div
        ref="input"
        class="composer"
        :class="{ glow: info.input_glow }"
        @compositionstart="composing = true"
        @compositionend="composing = false"
      >
        <div v-if="showHint" class="hint" aria-hidden="true">
          <Transition name="hint" mode="out-in">
            <span :key="hint">{{ t('assistant.tryAsk', { q: hint }) }}</span>
          </Transition>
        </div>
        <a-textarea
          v-model="draft"
          class="composer-input"
          :placeholder="showHint ? '' : t('assistant.placeholder')"
          :auto-size="{ minRows: 1, maxRows: 6 }"
          :max-length="4000"
          @keydown="onKey"
        />
        <div class="composer-row">
          <span class="left">{{ left !== null ? t('assistant.left', { n: left }) : '' }}</span>
          <a-button v-if="busy" class="send" type="primary" :aria-label="t('assistant.stop')" @click="stop">
            <template #icon><IconRecordStop /></template>
          </a-button>
          <a-button
            v-else
            class="send"
            type="primary"
            :disabled="!draft.trim() && !showHint"
            :aria-label="t('assistant.send')"
            @click="ask(draft || (showHint ? hint : ''))"
          >
            <template #icon><IconArrowUp /></template>
          </a-button>
        </div>
      </div>
      <div class="disclaimer">{{ t('assistant.disclaimer') }}</div>
    </footer>
  </section>
</template>

<style scoped>
/* Flat like the console's cards, its corners a little round (the user's
   choice for the assistant); a layer over the page, so it keeps the light
   shadow Arco's overlays have. */
.assistant-panel {
  position: fixed;
  right: 88px;
  bottom: 24px;
  z-index: 1001;
  display: flex;
  flex-direction: column;
  width: 400px;
  height: min(720px, calc(100vh - 96px));
  overflow: hidden;
  border: 1px solid var(--color-border-2);
  /* A little round, the frame of rounded insides (the user's choice). */
  border-radius: 10px;
  background: var(--color-bg-2);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.12);
}
/* Closed, it fades where it is rather than leave the page: its tooltips
   keep their place as they go. Opening, it rises a little into place. */
.assistant-panel {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease,
    visibility 0s;
}
.assistant-panel.closed {
  visibility: hidden;
  opacity: 0;
  transform: translateY(8px);
  pointer-events: none;
  transition:
    opacity 0.16s ease,
    transform 0.16s ease,
    visibility 0s linear 0.16s;
}
.assistant-panel.expanded {
  width: min(760px, calc(100vw - 120px));
  height: calc(100vh - 96px);
}
/* Opened from the navbar: hung under it, at the right. */
.assistant-panel.from-navbar {
  top: 68px;
  right: 16px;
  bottom: auto;
  height: min(720px, calc(100vh - 84px));
}
.assistant-panel.from-navbar.expanded {
  height: calc(100vh - 84px);
}
/* A phone: the whole screen, wherever it was opened from. */
.assistant-panel.phone.from-navbar {
  inset: 0;
  height: auto;
}
.assistant-panel.phone {
  inset: 0;
  width: auto;
  height: auto;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
.head {
  display: flex;
  flex: none;
  align-items: center;
  gap: 4px;
  height: 56px;
  padding: 0 12px 0 20px;
}
.title {
  flex: 1;
  min-width: 0;
  margin-left: 6px;
  overflow: hidden;
  font-size: 16px;
  font-weight: 500;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--color-text-1);
}
.tool {
  color: var(--color-text-2);
}
.tool.on {
  color: rgb(var(--primary-6));
}
.tool:disabled {
  color: var(--color-text-4);
}
.body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
}
.block {
  display: block;
}

/* ---- home ---- */
.home {
  /* At the end, the last row clears the fade. */
  padding-bottom: 32px;
  -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
  mask-image: linear-gradient(to bottom, #000 calc(100% - 24px), transparent);
}
.hello-title {
  margin-top: 12px;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: var(--color-text-1);
}
.hello-sub {
  margin-top: 4px;
  font-size: 13px;
  line-height: 20px;
  color: var(--color-text-3);
}
.section {
  margin: 20px 0 8px;
  font-size: 13px;
  color: var(--color-text-3);
}
/* 我能帮你做 and the box below are rounded where the console's cards are
   square: the assistant's own surfaces (the user's choice, 2026-10-28).
   The first card is drawn out — the assistant's violet washing in, its
   picture half out of the corner — the others plain. */
.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.action {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  min-height: 64px;
  padding: 0 16px;
  overflow: hidden;
  border: none;
  border-radius: 12px;
  background: var(--color-fill-1);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s;
}
.action:hover {
  background: var(--color-fill-2);
}
.action.featured {
  background: linear-gradient(100deg, rgba(138, 92, 255, 0.16) 0%, rgba(138, 92, 255, 0.07) 55%, rgba(47, 107, 255, 0.04) 100%);
}
.action.featured:hover {
  background: linear-gradient(100deg, rgba(138, 92, 255, 0.23) 0%, rgba(138, 92, 255, 0.11) 55%, rgba(47, 107, 255, 0.06) 100%);
}
.action-icon {
  position: relative;
  z-index: 1;
  color: var(--color-text-1);
}
.action-art {
  position: absolute;
  right: -12px;
  bottom: -20px;
}
.action-text {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.action-title {
  font-size: 15px;
  font-weight: 500;
  line-height: 22px;
  color: var(--color-text-1);
}
.featured .action-title {
  color: #4d36c9;
}
body[arco-theme='dark'] .featured .action-title {
  color: #b9adff;
}
.action-sub {
  font-size: 12px;
  line-height: 20px;
  color: var(--color-text-3);
}
.suggestions {
  overflow: hidden;
  border: 1px solid var(--color-border-2);
  border-radius: 12px;
}
.suggestion {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 40px;
  padding: 0 14px;
  border: none;
  background: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.suggestion + .suggestion {
  border-top: 1px solid var(--color-border-1);
}
.suggestion:hover {
  background: var(--color-fill-1);
}
.suggestion-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 13px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--color-text-1);
}
.suggestion-arrow {
  flex: none;
  color: var(--color-text-4);
}
.suggestion:hover .suggestion-text,
.suggestion:hover .suggestion-arrow {
  color: rgb(var(--primary-6));
}

/* ---- a conversation ---- */
.chat {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.q {
  display: flex;
  justify-content: flex-end;
}
.q-text {
  max-width: 85%;
  padding: 8px 12px;
  border-radius: 12px 12px 4px 12px;
  background: rgb(var(--primary-1));
  font-size: 14px;
  line-height: 22px;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--color-text-1);
}
.a {
  display: flex;
  gap: 10px;
}
.a-mark {
  margin-top: 1px;
}
.a-main {
  flex: 1;
  min-width: 0;
}
.waiting {
  font-size: 14px;
  line-height: 24px;
  color: var(--color-text-3);
}
.dots {
  display: inline-flex;
  gap: 3px;
  margin-left: 4px;
}
.dots i {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  animation: assistant-dot 1.2s infinite ease-in-out;
}
.dots i:nth-child(2) {
  animation-delay: 0.15s;
}
.dots i:nth-child(3) {
  animation-delay: 0.3s;
}
@keyframes assistant-dot {
  0%,
  80%,
  100% {
    opacity: 0.25;
  }
  40% {
    opacity: 1;
  }
}
.a-error {
  font-size: 13px;
  line-height: 22px;
  color: rgb(var(--danger-6));
}
.a-retry {
  margin-left: 8px;
  color: rgb(var(--primary-6));
  cursor: pointer;
}
.a-note {
  margin-top: 4px;
  font-size: 12px;
  color: var(--color-text-3);
}
.a-tools {
  display: flex;
  gap: 4px;
  margin-top: 4px;
  margin-left: -6px;
}
.a-tools :deep(.arco-btn) {
  color: var(--color-text-3);
}

/* The answer's Markdown. */
.md {
  font-size: 14px;
  line-height: 24px;
  overflow-wrap: anywhere;
  color: var(--color-text-1);
}
.md :deep(p) {
  margin: 0 0 8px;
}
.md :deep(> :last-child) {
  margin-bottom: 0;
}
.md :deep(h3),
.md :deep(h4),
.md :deep(h5),
.md :deep(h6) {
  margin: 12px 0 6px;
  font-size: 15px;
  font-weight: 600;
  line-height: 24px;
}
.md :deep(ul),
.md :deep(ol) {
  margin: 0 0 8px;
  padding-left: 20px;
}
.md :deep(li) {
  margin: 2px 0;
}
.md :deep(li > ul),
.md :deep(li > ol) {
  margin: 2px 0 0;
}
.md :deep(code) {
  padding: 1px 5px;
  background: var(--color-fill-2);
  font-family: SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
}
.md :deep(pre) {
  margin: 0 0 8px;
  padding: 10px 12px;
  overflow-x: auto;
  background: var(--color-fill-1);
  line-height: 20px;
}
.md :deep(pre code) {
  padding: 0;
  background: none;
}
.md :deep(blockquote) {
  margin: 0 0 8px;
  padding-left: 10px;
  border-left: 3px solid var(--color-border-3);
  color: var(--color-text-2);
}
.md :deep(a) {
  color: rgb(var(--primary-6));
}
.md :deep(hr) {
  margin: 12px 0;
  border: none;
  border-top: 1px solid var(--color-border-2);
}
.md :deep(.md-table) {
  margin: 0 0 8px;
  overflow-x: auto;
}
.md :deep(table) {
  border-collapse: collapse;
  font-size: 13px;
}
.md :deep(th),
.md :deep(td) {
  padding: 6px 10px;
  border: 1px solid var(--color-border-2);
  text-align: left;
}
.md :deep(th) {
  background: var(--color-fill-1);
  font-weight: 500;
}
/* The caret at the end of an answer being written. */
.md :deep(.md-caret) {
  display: inline-block;
  width: 7px;
  height: 15px;
  margin-left: 2px;
  vertical-align: -2px;
  background: rgb(var(--primary-6));
  animation: assistant-caret 1s steps(1) infinite;
}
@keyframes assistant-caret {
  50% {
    opacity: 0;
  }
}

/* ---- history ---- */
.history-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.back {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  color: var(--color-text-2);
  cursor: pointer;
}
.back:hover {
  color: rgb(var(--primary-6));
}
.history-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-1);
}
.history-item {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 44px;
  padding: 0 4px 0 12px;
  cursor: pointer;
}
.history-item + .history-item {
  border-top: 1px solid var(--color-border-1);
}
.history-item:hover,
.history-item.on {
  background: var(--color-fill-1);
}
.history-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: 13px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--color-text-1);
}
.history-time {
  flex: none;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  color: var(--color-text-3);
}
.history-del {
  flex: none;
  color: var(--color-text-3);
  visibility: hidden;
}
.history-item:hover .history-del {
  visibility: visible;
}
.empty {
  padding-top: 64px;
}

/* ---- the composer ---- */
.foot {
  flex: none;
  padding: 12px 16px 10px;
}
.composer {
  position: relative;
  border: 1px solid var(--color-border-2);
  border-radius: 14px;
  background: var(--color-bg-2);
  transition: border-color 0.15s;
}
.composer:focus-within {
  border-color: rgb(var(--primary-6));
}
/* 跑马灯's first kind (the admin's choice): a band of the assistant's
   colours running round the border. */
@property --assistant-glow {
  syntax: '<angle>';
  inherits: false;
  initial-value: 0deg;
}
.composer.glow,
.composer.glow:focus-within {
  border: 1.5px solid transparent;
  background:
    linear-gradient(var(--color-bg-2), var(--color-bg-2)) padding-box,
    conic-gradient(from var(--assistant-glow), #2f6bff, #8a5cff, #ff6fb5, #ffb547, #2f6bff) border-box;
  animation: assistant-glow 3.2s linear infinite;
}
@keyframes assistant-glow {
  to {
    --assistant-glow: 360deg;
  }
}
@media (prefers-reduced-motion: reduce) {
  .composer.glow {
    animation: none;
  }
}
/* 跑马灯's second kind: the suggestion showing, turning over upward. */
.hint {
  position: absolute;
  top: 11px;
  right: 14px;
  left: 14px;
  overflow: hidden;
  font-size: 14px;
  line-height: 22px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--color-text-3);
  pointer-events: none;
}
.hint span {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: top;
}
.hint-enter-active,
.hint-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}
.hint-enter-from {
  transform: translateY(12px);
  opacity: 0;
}
.hint-leave-to {
  transform: translateY(-12px);
  opacity: 0;
}
.composer-input {
  border: none;
  background: transparent;
}
.composer-input :deep(.arco-textarea) {
  padding: 10px 14px 0;
  font-size: 14px;
  line-height: 22px;
}
.composer-input:deep(.arco-textarea-wrapper),
.composer-input.arco-textarea-wrapper {
  border: none;
  background: transparent;
  box-shadow: none;
}
.composer-input :deep(.arco-textarea-word-limit) {
  display: none;
}
.composer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 8px 8px 14px;
}
.left {
  font-size: 12px;
  color: var(--color-text-4);
}
.send {
  flex: none;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 10px;
}
.disclaimer {
  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  color: var(--color-text-4);
}
</style>
